import { IconButton } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { AvpError } from '~/app/compositions/content/gallery/avp/avp-error';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { marketTo3DigitCountryCode } from '~/app/utils/market-to-3digit-countrycode';
import { aveRenderStringAtom } from '~/graphql/hooks/configurator-context';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';

/**
 * Please note that if AVP should be displayed, the AVP session ID must be set in the AvailableAvpViews in the ctx-configurator.tsx file. This approach is needed for multi-session support, but I am open for better approaches which does not require that.
 */

export const Avp = ({
	id,
	aspectRatio,
	trackAvpClick,
}: {
	id: string;
	aspectRatio?: string;
	trackAvpClick: (open: boolean) => void;
}) => {
	const {
		active3DViews,
		AVPSessionId,
		hideAVP,
		hasAVPError,
		setHideAVP,
		setHasAVPError,
		setActive3DViews,
		AVPConfigId,
		AVPCameraId,
	} = useCtxConfigurator();
	const aveRenderString = useAtomValue(aveRenderStringAtom);
	const { market } = useMarketConfig();
	const marketIsoCode = marketTo3DigitCountryCode[market];
	const { isMobile, isMobileView } = useBreakPoint();
	const { i18n } = useI18n();

	const handleStopStream = () => {
		setHideAVP(true);
		const avpShadowRoot = document?.querySelector('avp-3dws-deck')?.shadowRoot;
		const stopButton = avpShadowRoot?.querySelector('button[class*="deck-icon-stop"]') as HTMLButtonElement;
		stopButton?.click();
		trackAvpClick(false);
	};

	const handleBackAfterError = () => {
		setActive3DViews([]);
		setHasAVPError(false);
		handleStopStream();
	};

	return (
		<>
			{active3DViews.includes(id) && (
				<>
					<avp-3dws-deck
						camera={AVPCameraId}
						config-id={AVPConfigId}
						active={active3DViews.includes(id)}
						market={marketIsoCode}
						session-id={AVPSessionId}
						pr-string={aveRenderString}
						style={{
							display: hideAVP ? 'none' : 'block',
							aspectRatio: aspectRatio ? aspectRatio : isMobile || isMobileView ? '4 / 3' : '15 / 9',
							width: '100%',
						}}
					/>
					{!hideAVP && hasAVPError && <AvpError onBackAfterError={handleBackAfterError} />}
					{!hideAVP && active3DViews.includes(id) && AVPSessionId && (
						<IconButton
							className="control-button close-avp"
							variant="tertiary"
							size="sm"
							icon={'cancel'}
							aria-label={i18n('ngc.actions.closeAVP')}
							onClick={handleStopStream}
						/>
					)}
				</>
			)}
		</>
	);
};
